import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { NumberInput } from "../../ui/number-input";
import { formatCurrency } from "../../../utils/formatters";
import {
  buildMaterialClassificationOptions,
  FINISH_MODE_OPTIONS,
  inferMaterialClassification,
} from "../../../utils/materialFlow";
import { toNumberOrNull } from "./qboLine";
import { childDisplayName, childList } from "./childModel";
import {
  ZERO_LABOR_CONFIRMED_NOTE,
  hasNegativeLaborOverride,
  hasZeroLaborOverride,
} from "./traceLabels";
import Disclosure from "../Disclosure";
import LaborOverrideAlert from "./LaborOverrideAlert";
import { describeLineError } from "./lineErrors";
import "./estimate-children.css";

const PRICE_BASIS_OPTIONS = [
  { value: "per_unit", label: "Each" },
  { value: "per_line", label: "For the line" },
  { value: "per_sqft", label: "Per sq ft" },
];

const LABOR_FIELDS = [
  { field: "cnc_hours", label: "CNC hours" },
  { field: "cut_hours", label: "Cut hours" },
  { field: "fab_hours", label: "Fabrication hours" },
  { field: "finish_hours", label: "Coatings hours" },
];

function resultForChild(result, childIndex) {
  const children = Array.isArray(result?.children) ? result.children : [];
  return (
    children.find((child) => child?.child_index === childIndex) ||
    children[childIndex] ||
    null
  );
}

function ChildCard({
  child,
  childIndex,
  lineIndex,
  dispatch,
  availableProductTypes,
  availableMaterials,
  parentTypeName,
  pricedResult,
  onRemove,
  openByDefault = false,
}) {
  const productType = (availableProductTypes || []).find(
    (type) => type.key === child?.product_type_key,
  );
  const requiredDims = Array.isArray(productType?.required_dimensions)
    ? productType.required_dimensions
    : null;
  const showDim = (dimension) =>
    !requiredDims ||
    requiredDims.length === 0 ||
    requiredDims.includes(dimension);

  const allClassOptions = useMemo(
    () => buildMaterialClassificationOptions(availableMaterials || []),
    [availableMaterials],
  );
  const compatible = productType?.compatible_material_classes;
  const classOptions =
    Array.isArray(compatible) && compatible.length
      ? allClassOptions.filter((option) => compatible.includes(option.value))
      : allClassOptions;
  const sheetOptions = (availableMaterials || []).filter((material) => {
    if (!child?.material_classification) return true;
    return (
      inferMaterialClassification(material?.name) ===
      child.material_classification
    );
  });
  const hasOverride =
    child?.material_price_override !== null &&
    child?.material_price_override !== undefined &&
    child?.material_price_override !== "";
  const materialFailed = Boolean(pricedResult?.material_error);
  const uncarded = Boolean(
    pricedResult?.rate_card_resolved?.labor_card_missing,
  );

  const update = (field, value) =>
    dispatch({
      type: "UPDATE_LINE_ITEM_CHILD_FIELD",
      index: lineIndex,
      childIndex,
      field,
      value,
    });

  const prefix = `child-${lineIndex}-${childIndex}`;

  /* Read once, at mount. `openByDefault` is "you just asked for this one", and
     it stops being true the moment a NEXT component is added — as a live prop
     that would slam the previous card shut under the estimator's hands. */
  const [openedOnMount] = useState(openByDefault);

  /* The states that must never sit behind a closed card. Every child warning
     lives in this list; a new one added to childModel/childTrace belongs here
     too, or the collapse would hide it. */
  const laborNeedsAttention =
    hasNegativeLaborOverride(child) ||
    (hasZeroLaborOverride(child) && !child?.zero_labor_confirmed);
  // A child the engine refused to price (`error`) contributes $0 to its
  // parent; it must never sit closed reading like a legitimate $0.00 price.
  // `child_error` is different: the child still prices (e.g. its own nested
  // children were ignored), so it opens the card with a warning but never
  // claims the child added nothing.
  const childError = pricedResult?.error || null;
  const childWarning = childError ? null : pricedResult?.child_error || null;
  const needsAttention = Boolean(
    childError ||
    childWarning ||
    materialFailed ||
    uncarded ||
    laborNeedsAttention,
  );
  const hasQuotedPrice =
    child?.material_price_override !== null &&
    child?.material_price_override !== undefined &&
    child?.material_price_override !== "";
  // The closed card's one line says what each figure is: material dollars
  // (quoted or catalog) and the shop hours the child adds to the parent.
  const childHours = toNumberOrNull(pricedResult?.labor_hours_line_total);
  const priceMeta = childError
    ? "Did not price"
    : pricedResult?.material_cost_line_total != null
      ? [
          `${formatCurrency(pricedResult.material_cost_line_total)} material${
            hasQuotedPrice ? " (quoted)" : ""
          }`,
          childHours ? `${Number(childHours.toFixed(2))} hr` : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : "—";

  // The nested hours panel follows the same one-way rule as Disclosure: a
  // zero/negative-hour alert opens it, typing 0.75 through "0" never closes it.
  const laborRef = useRef(null);
  useEffect(() => {
    if (laborNeedsAttention && laborRef.current && !laborRef.current.open) {
      laborRef.current.open = true;
    }
  }, [laborNeedsAttention]);

  return (
    <section
      className="est-child"
      aria-label={`Child component ${childIndex + 1}`}
    >
      <Disclosure
        className="est-child__disc"
        summary={
          <strong>{childDisplayName(child, availableProductTypes)}</strong>
        }
        meta={priceMeta}
        open={openedOnMount}
        forceOpen={needsAttention}
      >
        <div className="est-child__body">
          {childError ? (
            <p className="est-alert" role="alert">
              This component did not price — it adds $0.00 to the line.{" "}
              {describeLineError(childError).message}
            </p>
          ) : null}
          {childWarning ? (
            <p className="est-alert" role="alert">
              {describeLineError(childWarning).message}
            </p>
          ) : null}
          <div className="est-child__fields est-child__fields--identity">
            <div>
              <label htmlFor={`${prefix}-type`}>Product type</label>
              <select
                id={`${prefix}-type`}
                className="wb-input"
                value={child?.product_type_key || ""}
                onChange={(event) =>
                  update("product_type_key", event.target.value || null)
                }
              >
                <option value="" disabled>
                  Select product
                </option>
                {(availableProductTypes || []).map((type) => (
                  <option key={type.key} value={type.key}>
                    {type.display_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor={`${prefix}-name`}>Name (optional)</label>
              <Input
                id={`${prefix}-name`}
                className="h-9"
                value={child?.name || ""}
                onChange={(event) => update("name", event.target.value)}
              />
            </div>
          </div>

          <div className="est-child__fields">
            {["length", "width", "height"].map((dimension) =>
              showDim(dimension) ? (
                <div key={dimension}>
                  <label htmlFor={`${prefix}-${dimension}`}>
                    {dimension[0].toUpperCase() + dimension.slice(1)}
                  </label>
                  <div className="est-suffix">
                    <NumberInput
                      id={`${prefix}-${dimension}`}
                      align="right"
                      step="0.5"
                      value={child?.[dimension]}
                      onValueChange={(value) => update(dimension, value ?? 0)}
                      className="h-9"
                    />
                    <span>in</span>
                  </div>
                </div>
              ) : null,
            )}
            <div>
              <label htmlFor={`${prefix}-quantity`}>Quantity</label>
              <div className="est-suffix">
                <NumberInput
                  id={`${prefix}-quantity`}
                  align="right"
                  step="1"
                  min={0}
                  value={child?.quantity}
                  onValueChange={(value) => update("quantity", value ?? 1)}
                  className="h-9"
                  title="How many of these on each parent piece."
                />
                <span>per {parentTypeName}</span>
              </div>
            </div>
          </div>

          <div className="est-child__fields">
            <div>
              <label htmlFor={`${prefix}-class`}>Material class</label>
              <select
                id={`${prefix}-class`}
                className="wb-input"
                value={child?.material_classification || ""}
                onChange={(event) =>
                  update("material_classification", event.target.value || "")
                }
              >
                <option value="" disabled>
                  Select class
                </option>
                {classOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor={`${prefix}-sheet`}>
                Specific sheet (optional)
              </label>
              <select
                id={`${prefix}-sheet`}
                className="wb-input"
                value={child?.material_id ?? ""}
                onChange={(event) =>
                  update("material_id", event.target.value || null)
                }
              >
                <option value="">Auto (card default thickness)</option>
                {sheetOptions.map((material) => (
                  <option key={material.id} value={material.id}>
                    {material.name} · {formatCurrency(material.cost_per_sqft)}
                    /sqft
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor={`${prefix}-finish`}>Finish</label>
              <select
                id={`${prefix}-finish`}
                className="wb-input"
                value={child?.finish_mode || ""}
                onChange={(event) =>
                  update("finish_mode", event.target.value || null)
                }
              >
                <option value="" disabled>
                  Select finish
                </option>
                {FINISH_MODE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {materialFailed ? (
            <p className="est-child__alert" role="alert">
              No material was charged on this component — shop labor still
              priced.
            </p>
          ) : null}
          {uncarded ? (
            <p className="est-child__candidate">
              No rate card matches this component — its price files as a
              candidate for review.
            </p>
          ) : null}

          <div className="est-child__quoted">
            <div className="est-child__subhead">
              <span>Quoted price</span>
              {hasOverride ? (
                <span className="est-quoted__badge">Quoted price</span>
              ) : null}
            </div>
            <div className="est-child__fields">
              <div>
                <label htmlFor={`${prefix}-quoted-amount`}>Amount</label>
                <NumberInput
                  id={`${prefix}-quoted-amount`}
                  align="right"
                  step="0.01"
                  min={0}
                  value={child?.material_price_override ?? ""}
                  onValueChange={(value) =>
                    update("material_price_override", value)
                  }
                  className="h-9"
                />
              </div>
              <div>
                <label htmlFor={`${prefix}-quoted-basis`}>Basis</label>
                <select
                  id={`${prefix}-quoted-basis`}
                  className="wb-input"
                  value={child?.material_price_override_basis || "per_unit"}
                  onChange={(event) =>
                    update("material_price_override_basis", event.target.value)
                  }
                >
                  {PRICE_BASIS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor={`${prefix}-quoted-note`}>Note</label>
                <Input
                  id={`${prefix}-quoted-note`}
                  className="h-9"
                  value={child?.material_price_override_note || ""}
                  placeholder="Vendor / quote ref"
                  maxLength={240}
                  onChange={(event) =>
                    update("material_price_override_note", event.target.value)
                  }
                />
              </div>
            </div>
          </div>

          {/* Opening the card is only half the promise: the zero- and
              negative-hour alerts live in here, so the nested details opens
              with them. */}
          <details className="est-child__labor" ref={laborRef}>
            <summary>Hours for the whole line</summary>
            <div className="est-child__fields">
              {LABOR_FIELDS.map(({ field, label }) => (
                <div key={field}>
                  <label htmlFor={`${prefix}-${field}`}>{label}</label>
                  <NumberInput
                    id={`${prefix}-${field}`}
                    align="right"
                    step="0.1"
                    min={0}
                    value={child?.[field]}
                    onValueChange={(value) => update(field, value)}
                    className="h-9"
                  />
                </div>
              ))}
            </div>
            <LaborOverrideAlert
              item={child}
              lineIndex={lineIndex}
              childIndex={childIndex}
              dispatch={dispatch}
            />
            {hasZeroLaborOverride(child) &&
            child?.zero_labor_confirmed &&
            !hasNegativeLaborOverride(child) ? (
              <p className="est-override__quiet">{ZERO_LABOR_CONFIRMED_NOTE}</p>
            ) : null}
          </details>

          <div className="est-child__foot">
            <button type="button" className="wb-tbtn" onClick={onRemove}>
              Remove
            </button>
          </div>
        </div>
      </Disclosure>
    </section>
  );
}

function ChildComponentsPanel({
  lineItem,
  lineIndex,
  dispatch,
  availableProductTypes = [],
  availableMaterials = [],
  result = null,
}) {
  const children = childList(lineItem);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const parentTypeName =
    availableProductTypes.find(
      (type) => type.key === lineItem?.product_type_key,
    )?.display_name || "parent";

  /* A component you just asked for opens; the reducer appends, so its index
     is the length before the dispatch. Index-based because children have no
     stable ids yet (see the "Stable line IDs" backlog item). */
  const [justAdded, setJustAdded] = useState(null);
  const addChild = () => {
    setJustAdded(children.length);
    dispatch({ type: "ADD_LINE_ITEM_CHILD", index: lineIndex });
  };
  useEffect(() => {
    if (justAdded !== null && children.length < justAdded) setJustAdded(null);
  }, [children.length, justAdded]);
  const removeChild = (childIndex) => {
    const priced = resultForChild(result, childIndex);
    const money = toNumberOrNull(priced?.material_cost_line_total);
    if (money !== null && money > 0) {
      setPendingRemoval(childIndex);
      return;
    }
    dispatch({
      type: "REMOVE_LINE_ITEM_CHILD",
      index: lineIndex,
      childIndex,
    });
  };
  const confirmRemoval = () => {
    if (pendingRemoval === null) return;
    dispatch({
      type: "REMOVE_LINE_ITEM_CHILD",
      index: lineIndex,
      childIndex: pendingRemoval,
    });
    setPendingRemoval(null);
  };

  return (
    <fieldset className="est-fieldset est-children">
      <legend>Child components</legend>
      {children.length === 0 ? (
        <div className="est-children__empty">
          <span className="wb-hint">None yet.</span>
          <button
            type="button"
            className="wb-tbtn"
            onClick={addChild}
            title="A cap, base or neck molding priced as its own product type, under this line."
          >
            + Add component
          </button>
        </div>
      ) : (
        <>
          <div className="est-children__list">
            {children.map((child, childIndex) => (
              <ChildCard
                key={childIndex}
                child={child}
                childIndex={childIndex}
                lineIndex={lineIndex}
                dispatch={dispatch}
                availableProductTypes={availableProductTypes}
                availableMaterials={availableMaterials}
                parentTypeName={parentTypeName}
                pricedResult={resultForChild(result, childIndex)}
                onRemove={() => removeChild(childIndex)}
                openByDefault={childIndex === justAdded}
              />
            ))}
          </div>
          <button
            type="button"
            className="wb-tbtn est-children__add"
            onClick={addChild}
          >
            + Add component
          </button>
        </>
      )}

      <Dialog
        open={pendingRemoval !== null}
        onOpenChange={(open) => !open && setPendingRemoval(null)}
      >
        <DialogContent className="est-confirm">
          <DialogHeader>
            <DialogTitle>Remove this child component?</DialogTitle>
            <DialogDescription>
              This component has priced material on it. Removing it changes the
              parent line and job totals.
            </DialogDescription>
          </DialogHeader>
          <div className="est-confirm__actions">
            <button
              type="button"
              className="wb-tbtn"
              onClick={() => setPendingRemoval(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="wb-tbtn wb-tbtn--solid"
              onClick={confirmRemoval}
            >
              Remove component
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </fieldset>
  );
}

export default ChildComponentsPanel;
